// ReportDocument — structured 409A report + summary (F8.1, F8.2). Presentation.

import type { ValuationResult } from '@backsolve/shared-types';
import { usd, usdPrecise, pct, isoDate } from '../../format/format';

export function ReportDocument({ result }: { result: ValuationResult }) {
  const h = result.headline;
  return (
    <div className="card">
      <div className="card-title">409A Valuation Report</div>
      <section>
        <h3 className="report-section-heading">1. Purpose</h3>
        <p>
          This report documents the fair market value (FMV) of the common stock of{' '}
          <strong>{result.inputs.capTableSnapshot.entityName}</strong> as of{' '}
          <strong>{isoDate(result.inputs.compliance.valuationDate)}</strong>, for purposes of IRC §409A
          option strike-price determination.
        </p>
      </section>
      <section>
        <h3 className="report-section-heading">2. Standard of Value</h3>
        <p>
          Fair Market Value, defined as the price at which property would change hands between a
          hypothetical willing buyer and seller, neither under compulsion, both having reasonable
          knowledge of relevant facts.
        </p>
      </section>
      <section>
        <h3 className="report-section-heading">3. Methodology</h3>
        <p>
          The OPM-backsolve → Black-Scholes-allocation → DLOM pipeline was applied. Total equity value was
          solved such that the OPM-implied per-share value of the anchor class equals the observed
          transaction price. Method rationale: {result.inputs.assumptions.methodRationale || '(none provided)'}.
        </p>
      </section>
      <section>
        <h3 className="report-section-heading">4. Cap-Table Snapshot</h3>
        <p>
          Pinned to version {result.inputs.capTableSnapshot.version} ({result.inputs.capTableSnapshot.asOfDate}).
          Fully diluted shares: {result.headline.fullyDilutedShares.toLocaleString()}.
        </p>
      </section>
      <section>
        <h3 className="report-section-heading">5. Assumptions</h3>
        <ul>
          <li>Volatility: {pct(result.inputs.assumptions.volatility)}</li>
          <li>Time to liquidity: {result.inputs.assumptions.timeToLiquidity} years</li>
          <li>Risk-free rate: {pct(result.inputs.assumptions.riskFreeRate)}</li>
          <li>Dividend yield: {pct(result.inputs.assumptions.dividendYield)}</li>
          <li>DLOM: {pct(result.inputs.discounts.dlomPercentage)} ({result.inputs.discounts.dlomModel})</li>
        </ul>
      </section>
      <section>
        <h3 className="report-section-heading">6. Conclusion</h3>
        <p>
          The concluded common-stock FMV is <strong>{usdPrecise(h.commonFmvPerShare)}</strong> per share,
          with a recommended option strike price of <strong>{usdPrecise(h.recommendedStrikePrice)}</strong>.
          Implied total equity value: {usd(h.totalEquityValue)}.
          {h.commonToPreferredDiscount !== null &&
            ` Common-to-preferred discount: ${pct(h.commonToPreferredDiscount)}.`}
        </p>
      </section>
    </div>
  );
}

export function ReportSummary_({ result }: { result: ValuationResult }) {
  const h = result.headline;
  return (
    <div className="card">
      <div className="card-title">Report Summary (One-Page)</div>
      <div className="headline-grid">
        <div className="metric">
          <span className="metric-label">Entity</span>
          <span className="metric-value" style={{ fontSize: 'var(--font-size-2)' }}>
            {result.inputs.capTableSnapshot.entityName}
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">Common FMV</span>
          <span className="metric-value">{usdPrecise(h.commonFmvPerShare)}</span>
        </div>
        <div className="metric">
          <span className="metric-label">Recommended Strike</span>
          <span className="metric-value">{usdPrecise(h.recommendedStrikePrice)}</span>
        </div>
      </div>
    </div>
  );
}
